import React, { useState, useEffect } from 'react';
import { Table, Button, Select, DatePicker, Input, Form, Tag, Popconfirm, message, Card } from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getLeaveRecords, createLeaveRecord, decideLeaveRecord, deleteLeaveRecord } from '../../api/leaves';
import { apiErrorMessage } from '../../utils/apiError';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const TYPE_LABELS = {
  dogum_izni: 'Doğum İzni',
  yillik_izin: 'Yıllık İzin',
  mazeret_izni: 'Mazeret İzni',
  yarim_gun: 'Yarım Gün',
};

const STATUS_TAGS = {
  pending: <Tag color="gold">Bekliyor</Tag>,
  approved: <Tag color="green">Onaylandı</Tag>,
  rejected: <Tag color="red">Reddedildi</Tag>,
};

const UserLeavesTab = ({ userId, canManage }) => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const [form] = Form.useForm();

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await getLeaveRecords({ user_id: userId, per_page: 100 });
      setRecords(res.data.data || []);
    } catch {
      message.error('İzin kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      await createLeaveRecord({
        user_id: userId,
        type: values.type,
        start_at: values.range[0].format('YYYY-MM-DD HH:mm:ss'),
        end_at: values.range[1].format('YYYY-MM-DD HH:mm:ss'),
        note: values.note,
      });
      message.success('İzin kaydı eklendi.');
      form.resetFields();
      fetchRecords();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İzin kaydı oluşturulamadı.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDecide = async (id, decision) => {
    setDecidingId(id);
    try {
      await decideLeaveRecord(id, { decision });
      message.success(decision === 'approve' ? 'Onaylandı.' : 'Reddedildi.');
      fetchRecords();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setDecidingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLeaveRecord(id);
      message.success('Kayıt silindi.');
      setRecords(prev => prev.filter(r => r.id !== id));
    } catch {
      message.error('Kayıt silinemedi.');
    }
  };

  const columns = [
    { title: 'Tür', dataIndex: 'type', render: (t) => TYPE_LABELS[t] || t },
    {
      title: 'Tarih Aralığı',
      render: (_, r) => `${dayjs(r.start_at).format('DD.MM.YYYY HH:mm')} — ${dayjs(r.end_at).format('DD.MM.YYYY HH:mm')}`,
    },
    { title: 'Not', dataIndex: 'note', render: (n) => n || <span className="text-gray-400">-</span> },
    { title: 'Durum', dataIndex: 'status', render: (s) => STATUS_TAGS[s] },
    {
      title: 'Talep Eden',
      render: (_, r) => r.requester ? (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users/${r.requester.id}`)}>
          {r.requester.name} {r.requester.surname}
        </Button>
      ) : '-',
    },
    {
      title: 'Karar',
      render: (_, r) => r.decided_at ? (
        <span>
          {r.decider ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users/${r.decider.id}`)}>
              {r.decider.name} {r.decider.surname}
            </Button>
          ) : '-'}
          {' — '}{dayjs(r.decided_at).format('DD.MM.YYYY HH:mm')}
        </span>
      ) : <span className="text-gray-400">-</span>,
    },
    ...(canManage ? [{
      title: 'İşlemler',
      render: (_, r) => (
        <div className="flex gap-2">
          {r.status === 'pending' && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />} loading={decidingId === r.id} onClick={() => handleDecide(r.id, 'approve')}>
                Onayla
              </Button>
              <Button size="small" danger icon={<CloseOutlined />} loading={decidingId === r.id} onClick={() => handleDecide(r.id, 'reject')}>
                Reddet
              </Button>
            </>
          )}
          <Popconfirm title="Kaydı silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(r.id)} okText="Evet" cancelText="Hayır">
            <Button size="small" danger icon={<DeleteOutlined />}>Sil</Button>
          </Popconfirm>
        </div>
      ),
    }] : []),
  ];

  return (
    <div>
      {canManage && (
        <Card size="small" className="mb-4" title="Yeni İzin Kaydı">
          <Form form={form} layout="inline" onFinish={handleSubmit}>
            <Form.Item name="type" rules={[{ required: true, message: 'Tür seçiniz.' }]}>
              <Select placeholder="Tür" style={{ width: 150 }}>
                {Object.entries(TYPE_LABELS).map(([value, label]) => (
                  <Select.Option key={value} value={value}>{label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="range" rules={[{ required: true, message: 'Tarih aralığı seçiniz.' }]}>
              <RangePicker showTime format="DD.MM.YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="note">
              <TextArea placeholder="Not (opsiyonel)" autoSize style={{ width: 220 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" icon={<PlusOutlined />} htmlType="submit" loading={saving}>
                Ekle
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      <Table columns={columns} dataSource={records} rowKey="id" loading={loading} pagination={false} />
    </div>
  );
};

export default UserLeavesTab;
